/**
 * RegionalAdminRoutes.jsx — regional admin dashboard routes.
 * Shaped by: G2f owner 2026-10-12.
 * Invariants: MasterChart and SavoirCare follow the launcher catalog.
 *   Interpreter Requests stays open on this router.
 * Related: RequireLauncherApp.jsx, MasterChartDashboardRoutes.jsx.
 */
import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Settings from "components/dashboard/Settings";
import PromptsManagerPage from "pages/dashboard/admin/prompts/PromptsManagerPage";
import AllFilesPage from "pages/dashboard/admin/AllFilesPage";
import GlobalReadinessPage from "pages/admin/GlobalReadinessPage";
import DashboardLayout from "components/dashboard/DashboardLayout";
// Launcher targets for regional_admin are VisitStart, CareMap, and QueryManager
// (`/dashboard/prompts`). Other routes stay for direct links.
import { MasterChartDashboardRoutes } from "routes/MasterChartDashboardRoutes";
import { SavoirCareBoardPage } from "features/savoirCare";
import InterpreterRequestsPage from "pages/dashboard/facility/InterpreterRequestsPage";
import ReadinessDashboard from "pages/dashboard/readiness/ReadinessDashboard";
import InstitutionReportsPage from "pages/dashboard/reports/InstitutionReportsPage";
import VideoLibraryPage from "pages/dashboard/institutionRollout/VideoLibraryPage";
import InstitutionBrandPage from "pages/dashboard/institutionRollout/InstitutionBrandPage";
import DistributePage from "pages/dashboard/institutionRollout/DistributePage";
import RetiredChartMindRoute from "components/common/RetiredChartMindRoute";
import ChartMindPlusPage from "pages/chartmindPlus/ChartMindPlusPage";
import RequireLauncherApp from "components/appLauncher/RequireLauncherApp";
import ChartMindPlusDifferentialPage from "pages/chartmindPlus/ChartMindPlusDifferentialPage";
import { useAuth } from "hooks/useAuth";
import { PromptsManagerProvider } from "context/PromptsManager";
import { ChartMindSessionProvider } from "context/ChartMindSession";

/**
 * Routes only accept Route elements, so the shared MasterChart fragment is
 * cloned here. /health-records is inside that fragment.
 */
function guardMasterChartRoutes(fragment) {
  const children = fragment?.props?.children;
  if (!children) return fragment ?? null;
  return React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    return React.cloneElement(child, {
      element: (
        <RequireLauncherApp appId="masterchart">
          {child.props.element}
        </RequireLauncherApp>
      ),
    });
  });
}

/**
 * Manual role: regional_admin + users.region → scopeId for llmLocalPrompts (scope=region).
 */
const RegionalAdminRoutes = () => {
  const { user, subscription } = useAuth();
  const location = useLocation();
  const isChartMindSessionList = /\/chartmind\/sessions\/?$/.test(location.pathname);
  const chartMindFullWidth =
    location.pathname.includes("/chartmind") && !isChartMindSessionList;

  return (
    <DashboardLayout fullWidth={chartMindFullWidth}>
      <PromptsManagerProvider>
        <ChartMindSessionProvider>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard/prompts" replace />} />
            <Route
              path="/settings"
              element={<Settings uid={user?.uid} subscription={subscription} />}
            />
            <Route path="/prompts" element={<PromptsManagerPage />} />
            <Route path="/files" element={<AllFilesPage />} />
            <Route path="/admin/readiness" element={<GlobalReadinessPage />} />
            <Route path="/diskusage" element={<Navigate to="/dashboard/files" replace />} />
            {/* G2f: Regional Admin catalog is No for MasterChart and SavoirCare. */}
            {guardMasterChartRoutes(MasterChartDashboardRoutes())}
            <Route
              path="/chartmind"
              element={
                <RetiredChartMindRoute />
              }
            />
            <Route
              path="/chartmind-plus"
              element={
                <RequireLauncherApp appId="chartmind-plus">
                  <ChartMindPlusPage />
                </RequireLauncherApp>
              }
            />
            <Route
              path="/chartmind-plus/differential"
              element={
                <RequireLauncherApp appId="chartmind-plus">
                  <ChartMindPlusDifferentialPage />
                </RequireLauncherApp>
              }
            />
            <Route
              path="/chartmind/:sessionId"
              element={
                <RetiredChartMindRoute />
              }
            />
            <Route
              path="/savoircare"
              element={
                <RequireLauncherApp appId="savoircare">
                  <SavoirCareBoardPage />
                </RequireLauncherApp>
              }
            />
            <Route
              path="/interpreter-requests"
              element={<InterpreterRequestsPage />}
            />
            <Route path="/reports" element={<InstitutionReportsPage />} />
            <Route path="/institution-rollout" element={<VideoLibraryPage />} />
            <Route
              path="/institution-rollout/brand"
              element={<InstitutionBrandPage />}
            />
            <Route
              path="/institution-rollout/distribute"
              element={<DistributePage />}
            />
            <Route path="/readiness" element={<ReadinessDashboard />} />
            <Route path="*" element={<Navigate to="/dashboard/prompts" replace />} />
          </Routes>
        </ChartMindSessionProvider>
      </PromptsManagerProvider>
    </DashboardLayout>
  );
};

export default RegionalAdminRoutes;
